'use client';

import React from 'react';
import { TourProvider as ReactourTourProvider, useTour as useReactourTour } from '@reactour/tour';
import { logger } from '@/lib/logger';
import { useTour } from '@/lib/tour-context';
import { useModalState } from '@/lib/modal-context';

// ModalState type (matches modal-context.tsx)
interface ModalState {
  isShockModalOpen: boolean;
  isBorrowingModalOpen: boolean;
}

// Shared variable for beforeOpen callback (module-level to persist across renders)
let beforeOpenCallback: ((target: Element | null) => Promise<void>) | null = null;

// Fallback beforeOpen callback that always resolves immediately
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const fallbackBeforeOpen = async (_target: Element | null): Promise<void> => {
  return Promise.resolve();
};

/**
 * Inner component that handles modal opening for modal tours
 */
function TourProviderInner({ children }: { children: React.ReactNode }) {
  const { activeTour } = useTour();
  const { currentStep, steps, setCurrentStep, isOpen } = useReactourTour();
  const { modalState, openModal, closeModal, subscribe } = useModalState();
  const isModalTour = activeTour === 'onboarding-shock-parameters' || activeTour === 'onboarding-borrowing-instruments';
  const clickHandlerRef = React.useRef<((e: Event) => void) | null>(null);
  const currentElementRef = React.useRef<Element | null>(null);
  const autoAdvanceTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const waitingForModalRef = React.useRef(false);
  const currentStepRef = React.useRef(currentStep);
  const stepsRef = React.useRef(steps);
  const modalStateRef = React.useRef(modalState);
  // React 19 Best Practice: Store cleanup refs at component level for proper cleanup
  const selectItemClickHandlerRef = React.useRef<((e: Event) => void) | null>(null);
  const valueObserverRef = React.useRef<MutationObserver | null>(null);
  
  // Keep refs in sync with current values
  React.useEffect(() => {
    currentStepRef.current = currentStep;
  }, [currentStep]);

  React.useEffect(() => {
    stepsRef.current = steps;
  }, [steps]);

  React.useEffect(() => {
    modalStateRef.current = modalState;
  }, [modalState]);


  // Track previous modal state using a ref to avoid effect re-runs
  const previousModalStateRef = React.useRef<ModalState>({ ...modalState });

  // Subscribe to modal state changes for auto-advance after button click
  // React 19 Best Practice: Use refs to avoid stale closures in subscription callbacks
  // Dependency array intentionally excludes currentStep and modalState to prevent unnecessary re-subscriptions
  React.useEffect(() => {
    // Use ref to check current step (avoids stale closure)
    const currentStepValue = currentStepRef.current;
    if (!isOpen || !isModalTour || currentStepValue !== 0) {
      waitingForModalRef.current = false;
      previousModalStateRef.current = { ...modalState };
      return;
    }

    // Initialize previous state from current state
    previousModalStateRef.current = { ...modalState };
    
    const unsubscribe = subscribe((state) => {
      // Only auto-advance if we're waiting for modal and it just opened
      if (!waitingForModalRef.current) {
        previousModalStateRef.current = { ...state };
        return;
      }

      const isShockTour = activeTour === 'onboarding-shock-parameters';
      const previousState = previousModalStateRef.current;
      const wasModalClosed = isShockTour 
        ? !previousState.isShockModalOpen && state.isShockModalOpen
        : !previousState.isBorrowingModalOpen && state.isBorrowingModalOpen;
      
      // Update previousState BEFORE checking conditions (for next callback)
      previousModalStateRef.current = { ...state };

      // Use ref to get latest currentStep value (avoid stale closure)
      const currentStepValueNow = currentStepRef.current;

      // Only advance if modal transitioned from closed to open
      if (wasModalClosed && currentStepValueNow === 0 && steps && currentStepValueNow < steps.length - 1) {
        logger.info('Modal opened after button click, auto-advancing tour', {
          activeTour,
          currentStep: currentStepValueNow,
          nextStep: currentStepValueNow + 1,
        });

        // Auto-advance after a short delay to ensure modal is fully rendered
        if (autoAdvanceTimeoutRef.current) {
          clearTimeout(autoAdvanceTimeoutRef.current);
        }

        // Auto-advance timeout setup (detailed in app logs only)

        autoAdvanceTimeoutRef.current = setTimeout(() => {
          // Use ref to get the latest currentStep value
          const currentStepValueInTimeout = currentStepRef.current;
          const currentModalState = modalStateRef.current;
          
          // Verify modal is actually open before advancing
          const isShockTour = activeTour === 'onboarding-shock-parameters';
          const isModalOpen = isShockTour 
            ? currentModalState.isShockModalOpen
            : currentModalState.isBorrowingModalOpen;
          
          // Get the next step selector to verify element exists
          const nextStepIndex = currentStepValueInTimeout + 1;
          const nextStep = steps?.[nextStepIndex];
          const nextStepSelector = typeof nextStep?.selector === 'string' ? nextStep.selector : null;
          
          // Verify element exists before advancing
          let elementExists = false;
          if (nextStepSelector) {
            const element = document.querySelector(nextStepSelector);
            elementExists = !!element;
            // Element check details in app logs only
          }
          
          if (currentStepValueInTimeout === 0 && steps && currentStepValueInTimeout < steps.length - 1 && isModalOpen) {
            // Only advance if element exists or if selector is not provided (body/fallback)
            if (elementExists || !nextStepSelector) {
              logger.info('Auto-advancing tour to step', { 
                activeTour, 
                fromStep: currentStepValueInTimeout, 
                toStep: nextStepIndex,
                elementExists,
                nextStepSelector
              });
              setCurrentStep(nextStepIndex);
            } else {
              logger.warn('Next step element not found, waiting longer', {
                activeTour,
                nextStepSelector,
                currentStep: currentStepValueInTimeout,
              });
              // Retry after a longer delay if element not found
              if (autoAdvanceTimeoutRef.current) {
                clearTimeout(autoAdvanceTimeoutRef.current);
              }
              autoAdvanceTimeoutRef.current = setTimeout(() => {
                const retryElement = nextStepSelector ? document.querySelector(nextStepSelector) : null;
                if (retryElement || !nextStepSelector) {
                  logger.info('Retry: Auto-advancing tour to step', { 
                    activeTour, 
                    fromStep: currentStepValueInTimeout, 
                    toStep: nextStepIndex 
                  });
                  setCurrentStep(nextStepIndex);
                } else {
                  logger.error('Retry failed: Next step element still not found', {
                    activeTour,
                    nextStepSelector,
                  });
                }
                waitingForModalRef.current = false;
                autoAdvanceTimeoutRef.current = null;
              }, 500);
              return; // Don't clear waitingForModalRef yet - we're retrying
            }
          } else {
              // Auto-advance condition not met (details in app logs)
          }
          waitingForModalRef.current = false;
          autoAdvanceTimeoutRef.current = null;
        }, 500); // Increased delay to ensure modal is fully rendered
      }
    });

    return () => {
      unsubscribe();
      // Don't clear waitingForModalRef or timeout here - let timeout complete if it's already set
      // The timeout will clear itself when it fires
      // Only clear if we're actually closing the tour (isOpen becomes false)
      if (!isOpen) {
        waitingForModalRef.current = false;
        if (autoAdvanceTimeoutRef.current) {
          clearTimeout(autoAdvanceTimeoutRef.current);
          autoAdvanceTimeoutRef.current = null;
        }
      }
    };
    // Note: We intentionally don't include currentStep or modalState in dependencies to avoid re-running effect
    // when they change. We use refs to get the latest values.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isModalTour, steps, setCurrentStep, activeTour, subscribe]);

  // Cleanup auto-advance timeout
  React.useEffect(() => {
    return () => {
      if (autoAdvanceTimeoutRef.current) {
        clearTimeout(autoAdvanceTimeoutRef.current);
        autoAdvanceTimeoutRef.current = null;
      }
    };
  }, []);

  // Set data attribute on body when tour is open (for Select component to detect)
  React.useEffect(() => {
    if (isOpen) {
      document.body.setAttribute('data-tour-active', 'true');
    } else {
      document.body.removeAttribute('data-tour-active');
    }
    return () => {
      document.body.removeAttribute('data-tour-active');
    };
  }, [isOpen]);

  // Auto-advance on click for interactive elements and Select value changes
  React.useEffect(() => {
    if (!isOpen || currentStep === undefined || !steps || currentStep >= steps.length - 1) {
      // Clean up previous listener
      if (clickHandlerRef.current && currentElementRef.current) {
        currentElementRef.current.removeEventListener('click', clickHandlerRef.current);
        clickHandlerRef.current = null;
        currentElementRef.current = null;
      }
      return;
    }

    // Get the current step's selector
    const stepSelector = steps[currentStep]?.selector;
    if (!stepSelector || stepSelector === 'body') {
      return;
    }

    // StepType selector can be string or Element - we need string for querySelector
    const selectorString = typeof stepSelector === 'string' ? stepSelector : null;
    if (!selectorString) {
      return;
    }

    // Find the target element
    const element = document.querySelector(selectorString);
    if (!element) {
      return;
    }

    // Check if element is interactive (button, link, select, input, etc.)
    const isInteractive = 
      element instanceof HTMLButtonElement ||
      element instanceof HTMLAnchorElement ||
      element instanceof HTMLSelectElement ||
      element instanceof HTMLInputElement ||
      element.getAttribute('role') === 'button' ||
      element.getAttribute('tabindex') !== null ||
      element.closest('button') !== null ||
      element.closest('a') !== null ||
      element.closest('[role="button"]') !== null;

    if (!isInteractive) {
      return;
    }

    // Check if this is a Radix UI Select component
    const selectRoot = element.closest('[data-radix-select-root]') || 
                       element.querySelector('[data-radix-select-root]');
    const isRadixSelect = !!selectRoot || element.closest('[data-radix-select-trigger]') !== null;

    // Create click handler
    const handleClick = (e: Event) => {
      // For Select triggers, don't auto-advance immediately - wait for value change
      if (isRadixSelect && (e.target as Element).closest('[data-radix-select-trigger]')) {
        // Don't auto-advance on trigger click, wait for item selection
        return;
      }
      
      // For modal tours on button step (step 0), open modal and wait for modal state change
      if (isModalTour && currentStep === 0 && selectorString?.includes('button')) {
        logger.info('Button clicked in modal tour - opening modal', {
          activeTour,
          currentStep,
          stepSelector: selectorString,
        });
        
        // Set flag to wait for modal to open
        waitingForModalRef.current = true;
        
        // Open the appropriate modal
        if (selectorString.includes('shock-parameters-button')) {
          openModal('shock-parameters');
        } else if (selectorString.includes('new-borrowing-button')) {
          openModal('borrowing-instruments');
        }
        
        // Don't auto-advance here - modal state subscription will handle it
        return;
      }
      
      // For other interactive elements, auto-advance after a short delay
      if (autoAdvanceTimeoutRef.current) {
        clearTimeout(autoAdvanceTimeoutRef.current);
      }
      
      autoAdvanceTimeoutRef.current = setTimeout(() => {
        if (currentStep !== undefined && steps && currentStep < steps.length - 1) {
          logger.info('Auto-advancing tour after user clicked highlighted element', {
            activeTour,
            currentStep,
            nextStep: currentStep + 1,
            selector: selectorString,
          });
          setCurrentStep(currentStep + 1);
        }
        autoAdvanceTimeoutRef.current = null;
      }, 100);
    };

    // For Radix Select, listen for value changes when SelectItem is clicked
    const handleSelectValueChange = () => {
      // Delay to allow Select to close and update
      if (autoAdvanceTimeoutRef.current) {
        clearTimeout(autoAdvanceTimeoutRef.current);
      }
      
      autoAdvanceTimeoutRef.current = setTimeout(() => {
        if (currentStep !== undefined && steps && currentStep < steps.length - 1) {
          logger.info('Auto-advancing tour after Select value changed', {
            activeTour,
            currentStep,
            nextStep: currentStep + 1,
            selector: selectorString,
          });
          setCurrentStep(currentStep + 1);
        }
        autoAdvanceTimeoutRef.current = null;
      }, 300);
    };

    // Clean up previous listener
    if (clickHandlerRef.current && currentElementRef.current) {
      currentElementRef.current.removeEventListener('click', clickHandlerRef.current);
    }

    // Add click listener (but skip for Select triggers)
    if (!isRadixSelect || !element.closest('[data-radix-select-trigger]')) {
      element.addEventListener('click', handleClick, { once: true });
      clickHandlerRef.current = handleClick;
      currentElementRef.current = element;
    }

    // React 19 Best Practice: Clean up previous listeners before setting up new ones
    // This prevents memory leaks when effect re-runs with different conditions
    if (selectItemClickHandlerRef.current) {
      document.removeEventListener('click', selectItemClickHandlerRef.current, true);
      selectItemClickHandlerRef.current = null;
    }
    if (valueObserverRef.current) {
      valueObserverRef.current.disconnect();
      valueObserverRef.current = null;
    }

    // For Radix Select, listen for value changes
    if (isRadixSelect) {
      // Find the Select root and SelectValue elements
      const selectRootElement = element.closest('[data-radix-select-root]') || 
                                 (element.parentElement?.closest('[data-radix-select-root]'));
      
      // Find SelectValue - it could be in the trigger or in the root
      const selectValueElement = element.querySelector('[data-radix-select-value]') ||
                                 selectRootElement?.querySelector('[data-radix-select-value]') ||
                                 document.querySelector(`[data-radix-select-value]`);

      // Method 1: Listen for clicks on SelectItem elements
      // React 19 Best Practice: Event handler uses closure to access latest values from effect dependencies
      const handleSelectItemClick = (e: Event) => {
        const target = e.target as Element;
        
        // CRITICAL: Don't interfere with dropdown menu interactions
        // Check if click is on any dropdown menu element (not just user menu)
        // This prevents the tour provider from interfering with Radix UI's click-outside detection
        const isDropdownMenu = target.closest('[data-radix-dropdown-menu-content]') || 
                              target.closest('[data-radix-dropdown-menu-trigger]') ||
                              target.closest('[data-radix-dropdown-menu-item]') ||
                              target.closest('[data-radix-dropdown-menu-label]') ||
                              target.closest('[data-radix-dropdown-menu-separator]') ||
                              target.closest('[data-tour="user-menu"]');
        
        // If this is a dropdown menu interaction, return early to avoid interference
        // This prevents the tour provider from interfering with dropdown click-outside detection
        if (isDropdownMenu) {
        // Ignoring dropdown menu click (details in app logs)
          return; // Don't process this click - let dropdown handle it
        }
        
        // Diagnostic logging: Track clicks (excluding dropdown menus)
        // Document click handler (details in app logs)
        
        // Check if clicked element is a SelectItem (Radix uses [role="option"])
        const selectItem = target.closest('[role="option"]');
        
        if (selectItem) {
          // Verify SelectContent is open (to avoid false positives)
          const selectContent = document.querySelector('[data-radix-select-content][data-state="open"]');
          if (selectContent) {
            // SelectItem clicked, advancing tour (details in app logs)
            handleSelectValueChange();
          }
        }
      };

      // Store handler in ref for cleanup
      selectItemClickHandlerRef.current = handleSelectItemClick;

      // Method 2: Observe SelectValue element for text changes (most reliable)
      if (selectValueElement) {
        let previousValue = selectValueElement.textContent || '';
        let hasInitialized = false;
        
        // Observing SelectValue for changes (details in app logs)
        
        const valueObserver = new MutationObserver(() => {
          if (!isOpen || !activeTour) {
            return;
          }
          
          const currentValue = selectValueElement.textContent || '';
          
          // Skip if this is the first observation (initial render)
          if (!hasInitialized) {
            previousValue = currentValue;
            hasInitialized = true;
            // SelectValue observer initialized (details in app logs)
            return;
          }
          
          // Only trigger if value actually changed and is not empty
          if (currentValue !== previousValue && currentValue.trim() !== '' && previousValue.trim() !== '') {
            logger.info('SelectValue changed, will advance tour', { 
              activeTour, 
              currentStep,
              selector: stepSelector,
              previousValue,
              currentValue
            });
            previousValue = currentValue;
            handleSelectValueChange();
          }
        });

        valueObserver.observe(selectValueElement, {
          childList: true,
          subtree: true,
          characterData: true,
        });

        // Store observer in ref for cleanup
        valueObserverRef.current = valueObserver;
      } else {
        logger.warn('SelectValue element not found for tour step', {
          activeTour,
          currentStep,
          selector: stepSelector,
        });
      }

      // Listen for clicks on SelectItem elements (they're in a portal, so listen on document)
      document.addEventListener('click', handleSelectItemClick, true);
    }

    // React 19 Best Practice: Single cleanup function that handles all listeners
    // This ensures cleanup happens even when conditions change between effect runs
    return () => {
      // Clean up element click listener
      if (clickHandlerRef.current && currentElementRef.current) {
        currentElementRef.current.removeEventListener('click', clickHandlerRef.current);
        clickHandlerRef.current = null;
        currentElementRef.current = null;
      }
      
      // Clean up document click listener (for Select items)
      if (selectItemClickHandlerRef.current) {
        document.removeEventListener('click', selectItemClickHandlerRef.current, true);
        selectItemClickHandlerRef.current = null;
      }
      
      // Clean up MutationObserver
      if (valueObserverRef.current) {
        valueObserverRef.current.disconnect();
        valueObserverRef.current = null;
      }
      
      // Clean up timeout
      if (autoAdvanceTimeoutRef.current) {
        clearTimeout(autoAdvanceTimeoutRef.current);
        autoAdvanceTimeoutRef.current = null;
      }
    };
  }, [isOpen, currentStep, steps, setCurrentStep, activeTour, isModalTour, openModal]);

  // Handle comparison tour tab switching - watch for step 0 and proactively switch tab
  // React 19 Best Practice: Uses custom events for cross-component communication
  // Dependency array includes all values we check (activeTour, currentStep, steps)
  React.useEffect(() => {
    if (activeTour === 'onboarding-scenario-comparison' && currentStep === 0 && steps && steps.length > 1) {
      const nextStepSelector = steps[1]?.selector;
      const nextStepIsComparisonCharts = typeof nextStepSelector === 'string' && 
                                         nextStepSelector.includes('comparison-charts');
      
      if (nextStepIsComparisonCharts) {
        const comparisonTab = document.querySelector('[data-tour="scenario-comparison-tab"]');
        if (comparisonTab) {
          const isComparisonTabActive = comparisonTab.getAttribute('data-state') === 'active' ||
                                       comparisonTab.getAttribute('aria-selected') === 'true';

          if (!isComparisonTabActive) {
            logger.info('Proactively switching to comparison tab (on step 0, next step is comparison charts)', { 
              activeTour, 
              currentStep,
              nextStepSelector: typeof nextStepSelector === 'string' ? nextStepSelector : null
            });
            
            // Dispatch custom event to switch tab (more reliable than clicking)
            // The scenario analysis page will listen for this event
            const switchTabEvent = new CustomEvent('tour-switch-tab', {
              detail: { tabValue: 'scenario-comparison' },
              bubbles: true
            });
            window.dispatchEvent(switchTabEvent);
            
            // Also try clicking as fallback
            const tabButton = comparisonTab as HTMLElement;
            
            // Remove pointer-events from tour overlay temporarily
            const tourMask = document.querySelector('.reactour__mask');
            if (tourMask) {
              (tourMask as HTMLElement).style.pointerEvents = 'none';
            }
            
            tabButton.click();
            
            // Restore pointer-events
            setTimeout(() => {
              if (tourMask) {
                (tourMask as HTMLElement).style.pointerEvents = '';
              }
            }, 100);
            
            // Wait a bit for React to update the DOM
            setTimeout(() => {
              const isNowActive = comparisonTab.getAttribute('data-state') === 'active' ||
                                 comparisonTab.getAttribute('aria-selected') === 'true';
              
              if (!isNowActive) {
                logger.warn('Tab did not switch after proactive switch, trying again', { activeTour });
                // Try again with event + click
                window.dispatchEvent(switchTabEvent);
                tabButton.click();
              } else {
                logger.info('Tab successfully switched proactively', { activeTour });
              }
            }, 500);
          }
        }
      }
    }
  }, [activeTour, currentStep, steps]);

  // Create beforeOpen callback - handles modal opening/closing and ensures proper element positioning
  // React 19 Best Practice: Uses refs (stepsRef, currentStepRef, modalStateRef) to get latest values
  // without causing unnecessary re-runs when these values change
  // Key improvements:
  // - Determines step index by matching target element to step selectors (more reliable than refs)
  // - Waits for modal layout to stabilize before allowing React Tour to position overlay
  // - Ensures target elements are properly positioned (dimensions > 0, position > 0) before proceeding
  React.useEffect(() => {
    const beforeOpen = async (target: Element | null): Promise<void> => {
      try {
        // Always resolve immediately if no activeTour - don't block tour from starting
        // This handles the case where beforeOpen is called before activeTour is set
        if (!activeTour) {
          // No active tour, allowing tour to proceed (details in app logs)
          return Promise.resolve();
        }

        // Check if this is a modal tour dynamically
        const currentIsModalTour = activeTour === 'onboarding-shock-parameters' || activeTour === 'onboarding-borrowing-instruments';
        
        // For modal tours, we need steps - but allow tour to start even if steps/currentStep are undefined
        // Use refs to get latest values (avoids stale closures)
        const currentSteps = stepsRef.current;
        const currentStepValue = currentStepRef.current;
        
        // If steps aren't set yet, allow tour to start - TourManager will set them
        if (!currentSteps) {
          // Tour steps not set yet (details in app logs)
          return Promise.resolve();
        }

        // If currentStep is undefined, this is likely the initial tour start - allow it
        // React Tour will set currentStep when it actually opens the step
        if (currentStepValue === undefined || currentStepValue === null) {
          // Tour starting (details in app logs)
          return Promise.resolve();
        }

        // Handle comparison tour tab switching for step 1 (fallback if proactive switch didn't work)
        // Check if we're about to open step 1 (comparison charts) - if so, switch tab first
        const isComparisonTour = activeTour === 'onboarding-scenario-comparison';
        const nextStepSelector = currentSteps && currentSteps.length > 1 ? currentSteps[1]?.selector : null;
        const nextStepIsComparisonCharts = typeof nextStepSelector === 'string' && 
                                           nextStepSelector.includes('comparison-charts');
        
        if (isComparisonTour && currentStepValue === 0 && nextStepIsComparisonCharts) {
          const comparisonTab = document.querySelector('[data-tour="scenario-comparison-tab"]');
          if (comparisonTab) {
            const isComparisonTabActive = comparisonTab.getAttribute('data-state') === 'active' ||
                                         comparisonTab.getAttribute('aria-selected') === 'true';

            if (!isComparisonTabActive) {
              logger.info('Switching to comparison tab in beforeOpen (fallback)', { 
                activeTour, 
                currentStep: currentStepValue,
                target: target ? (target as Element).tagName : null,
                nextStepSelector: typeof nextStepSelector === 'string' ? nextStepSelector : null
              });
              
              const tabButton = comparisonTab as HTMLElement;
              
              // Remove pointer-events from tour overlay temporarily to allow click
              const tourMask = document.querySelector('.reactour__mask');
              if (tourMask) {
                (tourMask as HTMLElement).style.pointerEvents = 'none';
              }
              
              // Click the tab button
              tabButton.click();
              
              // Restore pointer-events after a short delay
              setTimeout(() => {
                if (tourMask) {
                  (tourMask as HTMLElement).style.pointerEvents = '';
                }
              }, 100);
              
              // Wait for tab to switch and content to render
              // Use a longer delay to ensure React has time to update the DOM
              await new Promise(resolve => setTimeout(resolve, 800));
              
              // Verify tab switched
              const isNowActive = comparisonTab.getAttribute('data-state') === 'active' ||
                                 comparisonTab.getAttribute('aria-selected') === 'true';
              
              if (!isNowActive) {
                logger.warn('Tab did not switch after click in beforeOpen', { activeTour });
                // Try clicking again
                tabButton.click();
                await new Promise(resolve => setTimeout(resolve, 400));
              } else {
                logger.info('Tab successfully switched in beforeOpen', { activeTour });
              }
            }
          }
        }
        
        // For non-modal tours (except comparison which we handled above), close any open modals
        if (!currentIsModalTour) {
          // Close any open modals when transitioning to a non-modal tour step
          const currentModalState = modalStateRef.current;
          if (currentModalState.isShockModalOpen || currentModalState.isBorrowingModalOpen) {
            logger.info('Closing modals before non-modal tour step', { 
              activeTour, 
              currentStep: currentStepValue,
              isShockOpen: currentModalState.isShockModalOpen,
              isBorrowingOpen: currentModalState.isBorrowingModalOpen
            });
            closeModal('shock-parameters');
            closeModal('borrowing-instruments');
            // Wait a moment for modals to close
            await new Promise(resolve => setTimeout(resolve, 200));
          }
          return Promise.resolve();
        }

        // Determine which step is being opened by matching target element to step selectors
        // This is more reliable than using currentStepValue which might be stale during auto-advance
        let targetStepIndex: number | null = null;
        let targetSelector: string | null = null;
        
        if (target && currentSteps) {
          // Try to match target element to a step selector
          for (let i = 0; i < currentSteps.length; i++) {
            const step = currentSteps[i];
            const stepSelector = typeof step?.selector === 'string' ? step.selector : null;
            if (stepSelector) {
              // Check if target element matches this step's selector
              try {
                const matchingElement = document.querySelector(stepSelector);
                if (matchingElement === target) {
                  targetStepIndex = i;
                  targetSelector = stepSelector;
                  break;
                }
              } catch {
                // Invalid selector, skip
              }
            }
          }
        }
        
        // Fallback to currentStepValue if we couldn't match target
        const stepIndex = targetStepIndex !== null ? targetStepIndex : currentStepValue;
        const stepSelector = targetSelector || (currentSteps && currentSteps[stepIndex] ? (typeof currentSteps[stepIndex].selector === 'string' ? currentSteps[stepIndex].selector : null) : null);
        const selectorString = stepSelector;
        
        // Handle modal steps (steps 1+) - ensure correct modal is open, close others
        // Step 0 is the button step - don't open modal here, let user click button
        if (stepIndex > 0 && selectorString) {
          const isShockModal = selectorString.includes('shock-modal');
          const isBorrowingModal = selectorString.includes('borrowing-modal');
          
          // Check if modal is already open (use ref to get latest modal state)
          const currentModalState = modalStateRef.current;
          const isShockOpen = currentModalState.isShockModalOpen;
          const isBorrowingOpen = currentModalState.isBorrowingModalOpen;
          
          // Close the modal that's not needed for this step
          if (isShockModal && isBorrowingOpen) {
            logger.info('Closing borrowing modal before shock modal step', { 
              activeTour, 
              stepIndex,
              currentStep: currentStepValue 
            });
            closeModal('borrowing-instruments');
            await new Promise(resolve => setTimeout(resolve, 200));
          } else if (isBorrowingModal && isShockOpen) {
            logger.info('Closing shock modal before borrowing modal step', { 
              activeTour, 
              stepIndex,
              currentStep: currentStepValue 
            });
            closeModal('shock-parameters');
            await new Promise(resolve => setTimeout(resolve, 200));
          }
          
          // Open the required modal if not already open
          if (isShockModal || isBorrowingModal) {
            const shouldOpen = (isShockModal && !isShockOpen) || (isBorrowingModal && !isBorrowingOpen);
            
            if (shouldOpen) {
              logger.info('Opening modal before step', { 
                activeTour, 
                stepSelector: selectorString, 
                stepIndex,
                currentStep: currentStepValue 
              });
              
              if (isShockModal) {
                openModal('shock-parameters');
              } else {
                openModal('borrowing-instruments');
              }
              
              // Wait for the modal to open and the target element to be rendered and positioned
              // Use polling to check if the element exists and is properly positioned
              const maxWaitTime = 2000; // 2 seconds max
              const pollInterval = 50; // Check every 50ms
              const startTime = Date.now();
              
              while (Date.now() - startTime < maxWaitTime) {
                const element = document.querySelector(selectorString);
                if (element) {
                  // Check if element is visible and has dimensions (layout is stable)
                  const rect = element.getBoundingClientRect();
                  if (rect.width > 0 && rect.height > 0 && rect.top > 0 && rect.left > 0) {
                    logger.info('Target element found and positioned after modal open', { 
                      activeTour, 
                      stepSelector: selectorString, 
                      stepIndex,
                      currentStep: currentStepValue,
                      waitTime: Date.now() - startTime,
                      elementRect: { width: rect.width, height: rect.height, top: rect.top, left: rect.left }
                    });
                    // Give React a moment to finish rendering and layout to stabilize
                    await new Promise(resolve => setTimeout(resolve, 300));
                    break;
                  }
                }
                await new Promise(resolve => setTimeout(resolve, pollInterval));
              }
              
              // If element still not found or not positioned, log warning but continue
              const element = document.querySelector(selectorString);
              if (!element) {
                logger.warn('Target element not found after modal open, continuing anyway', { 
                  activeTour, 
                  stepSelector: selectorString, 
                  stepIndex,
                  currentStep: currentStepValue 
                });
              } else {
                const rect = element.getBoundingClientRect();
                if (rect.width === 0 || rect.height === 0) {
                  logger.warn('Target element found but has no dimensions, continuing anyway', { 
                    activeTour, 
                    stepSelector: selectorString, 
                    stepIndex,
                    currentStep: currentStepValue,
                    elementRect: { width: rect.width, height: rect.height, top: rect.top, left: rect.left }
                  });
                }
              }
            } else {
              // Modal is already open, but verify the target element exists and is properly positioned
              // This handles the case where we're moving to a different step in the same modal
              // or when the modal was opened by user click and we're transitioning to step 1
              // Even if element exists, we need to wait for modal layout to stabilize
              const maxWaitTime = 2000; // 2 seconds max
              const pollInterval = 50; // Check every 50ms
              const startTime = Date.now();
              let elementFound = false;
              
              while (Date.now() - startTime < maxWaitTime) {
                const element = document.querySelector(selectorString);
                if (element) {
                  // Check if element is visible and has dimensions (layout is stable)
                  const rect = element.getBoundingClientRect();
                  if (rect.width > 0 && rect.height > 0 && rect.top > 0 && rect.left > 0) {
                    logger.info('Target element found and positioned in already-open modal', { 
                      activeTour, 
                      stepSelector: selectorString, 
                      stepIndex,
                      currentStep: currentStepValue,
                      waitTime: Date.now() - startTime,
                      elementRect: { width: rect.width, height: rect.height, top: rect.top, left: rect.left }
                    });
                    // Give React a moment to finish rendering and layout to stabilize
                    await new Promise(resolve => setTimeout(resolve, 300));
                    elementFound = true;
                    break;
                  }
                }
                await new Promise(resolve => setTimeout(resolve, pollInterval));
              }
              
              // If element still not found or not positioned, log warning but continue
              if (!elementFound) {
                const finalElement = document.querySelector(selectorString);
                if (!finalElement) {
                  logger.warn('Target element not found in already-open modal, continuing anyway', { 
                    activeTour, 
                    stepSelector: selectorString, 
                    stepIndex,
                    currentStep: currentStepValue 
                  });
                } else {
                  const rect = finalElement.getBoundingClientRect();
                  logger.warn('Target element found but may not be properly positioned', { 
                    activeTour, 
                    stepSelector: selectorString, 
                    stepIndex,
                    currentStep: currentStepValue,
                    elementRect: { width: rect.width, height: rect.height, top: rect.top, left: rect.left }
                  });
                }
              }
            }
          } else {
            // Step doesn't require a modal - close any open modals
            if (isShockOpen || isBorrowingOpen) {
              logger.info('Closing modals before non-modal step', { 
                activeTour, 
                currentStep: currentStepValue,
                stepSelector: selectorString
              });
              closeModal('shock-parameters');
              closeModal('borrowing-instruments');
              await new Promise(resolve => setTimeout(resolve, 200));
            }
          }
        } else {
          // Step 0 (button step) - close any open modals if they're not needed
          // This handles the case where we're starting a modal tour but a modal is already open
          const currentModalState = modalStateRef.current;
          const isShockOpen = currentModalState.isShockModalOpen;
          const isBorrowingOpen = currentModalState.isBorrowingModalOpen;
          
          // Determine which modal this tour needs
          const needsShockModal = activeTour === 'onboarding-shock-parameters';
          const needsBorrowingModal = activeTour === 'onboarding-borrowing-instruments';
          
          // Close modals that aren't needed for this tour
          if (needsShockModal && isBorrowingOpen) {
            logger.info('Closing borrowing modal at start of shock parameters tour', { activeTour });
            closeModal('borrowing-instruments');
          } else if (needsBorrowingModal && isShockOpen) {
            logger.info('Closing shock modal at start of borrowing instruments tour', { activeTour });
            closeModal('shock-parameters');
          }
        }
        
        // For button step (step 0) or non-modal steps, resolve immediately
        return Promise.resolve();
      } catch (error) {
        logger.error('Error in beforeOpen callback', { error, activeTour, target });
        return Promise.resolve();
      }
    };

    // Store callback in module-level variable
    // Always set the callback - don't clear it on navigation or tour changes
    // This ensures the callback is always available when React Tour calls it
    beforeOpenCallback = beforeOpen;
    
    // Don't clear callback on effect re-run - let it persist across tour changes
    // This prevents race conditions where React Tour calls beforeOpen before the new callback is set
    // Only clear when component unmounts (which shouldn't happen in normal operation)
    return () => {
      // No cleanup needed - keep callback available
    };
  }, [activeTour, currentStep, steps, modalState, openModal, closeModal]);

  return <>{children}</>;
}

/**
 * Main Tour Provider component
 * Wraps the app with @reactour/tour provider
 * Custom styling to match our design system
 */
export function AppTourProvider({ children }: { children: React.ReactNode }) {
  const { activeTour } = useTour();
  const afterOpenCallbackRef = React.useRef<((target: Element | null) => void) | null>(null);

  // Set up afterOpen callback to handle modal verification and auto-advance
  React.useEffect(() => {
    // Store in ref for AppTourProvider to use
    // Note: This is a simplified approach - we'll handle modal auto-advance in TourProviderInner
    return () => {
      afterOpenCallbackRef.current = null;
    };
  }, []);

  return (
    <ReactourTourProvider
      afterOpen={(target) => {
        // Tour step opened (details in app logs)
      }}
      beforeClose={(target) => {
        // Tour step closing (details in app logs)
      }}
      {...({
        beforeOpen: async (target: Element | null) => {
          // Always use fallback if callback is not set - never block tours from starting
          // The callback might not be set yet during initial render or navigation
          const callback = beforeOpenCallback || fallbackBeforeOpen;
          try {
            // Add timeout to prevent hanging (1 second max - very short to avoid blocking)
            return await Promise.race([
              callback(target),
              new Promise<void>((resolve) => {
                setTimeout(() => {
                  // beforeOpen callback timed out (details in app logs)
                  resolve();
                }, 1000); // Very short timeout to prevent blocking
              }),
            ]);
          } catch (error) {
            logger.error('Error in beforeOpen callback', { error, target, hasCallback: !!beforeOpenCallback });
            // Always resolve - never block tours from starting
            return Promise.resolve();
          }
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } as any)}
      styles={{
        popover: (base) => ({
          ...base,
          '--reactour-accent': 'hsl(var(--primary))',
          borderRadius: '8px',
          padding: '20px',
          maxWidth: '400px',
          // Ensure tour popover appears above modals (z-50) and dropdowns (z-[10000])
          // Tour mask uses z-index 9999, popover should be above that
          zIndex: 10001,
          // Ensure popover and all its children are clickable
          pointerEvents: 'auto',
          position: 'fixed', // Ensure fixed positioning for proper stacking
        }),
        maskArea: (base) => ({ ...base, rx: 8 }),
        maskWrapper: (base) => ({
          ...base,
          // @reactour/tour uses z-index 9999 for mask by default
          // This ensures the mask stays below modals (z-50) and dropdowns (z-[10000])
          zIndex: 9999,
          // Ensure mask doesn't block clicks on popover
          pointerEvents: 'none',
        }),
        badge: (base) => ({ ...base, left: 'auto', right: '-0.8125em' }),
        controls: (base) => ({ 
          ...base, 
          marginTop: '20px',
          // Ensure controls are always clickable
          pointerEvents: 'auto',
          position: 'relative',
          zIndex: 10002, // Higher than popover to ensure buttons are on top
        }),
        close: (base) => ({ 
          ...base, 
          right: '1em', 
          top: '1em',
          // Ensure close button is always clickable
          pointerEvents: 'auto',
          // Don't change position - keep original positioning from base styles
        }),
      }}
      onClickMask={({ currentStep, steps, setIsOpen }) => {
        // Prevent closing tour during modal steps (steps 1+)
        // Step 0 is the button step (outside modal), steps 1+ are inside the modal
        // During modal steps, clicking the mask should not close the tour
        // because the user needs to complete the modal steps
        
        // Check if we're in a modal tour
        const isModalTourValue = activeTour === 'onboarding-shock-parameters' || activeTour === 'onboarding-borrowing-instruments';
        
        // If we're in a modal step (step > 0) of a modal tour, prevent closing
        if (isModalTourValue && currentStep !== undefined && currentStep > 0) {
          logger.info('Preventing tour close on mask click during modal step', { 
            activeTour, 
            currentStep, 
            totalSteps: steps?.length 
          });
          // Don't close - keep tour open during modal steps
          return;
        }
        
        // Allow clicking mask to close tour for non-modal steps or non-modal tours
        // This respects user autonomy - they can exit anytime when not in a modal step
        logger.info('Tour closed by user clicking mask', { currentStep, totalSteps: steps?.length });
        setIsOpen(false);
      }}
    >
      <TourProviderInner>{children}</TourProviderInner>
    </ReactourTourProvider>
  );
}
